// deps
import React, { ReactElement, useCallback } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";

// interfaces
import { CART_STATUS_CODES, GROUP_CART_STATUS_CODES } from "@cart-ui/interfaces";

// components
import { ErrorWidget } from "@swiggy-private/core-ui";
import { GroupCartBottomSheetInfo } from "./components";

// constants
import { GROUP_NO_LONGER_EXISTS_TEXT, ORDER_ON_THE_WAY_COMMS } from "@cart-ui/constants/GroupCart";

// styles
import { ErrorWidgetContainer, ToastContainer } from "./GroupCartErrorBoundary.style";

export interface IGroupCartError {
    children: React.ReactNode;
    softError?: string; // accounts for errors which needs to be shown as toast messages
    error: boolean;
    statusCode?: number;
    statusMessage?: string;
    titleMessage?: string;
    ctaText?: string;
    ctaAction?: string;
    onClickBack: () => void;
    resetSoftError: () => void;
    onErrorCtaAction: (action: string) => void;
    renderGenericError: (errPayload: object) => ReactElement;
}

const GroupCartErrorBoundary = (props: IGroupCartError) => {
    const handleBack = useCallback(() => {
        props.onClickBack();
    }, [props]);

    const renderGenericError = useCallback(() => {
        return props.renderGenericError({
            softError: props.softError,
            error: props.error,
            statusCode: props.statusCode,
            statusMessage: props.statusMessage,
            titleMessage: props.titleMessage,
            ctaText: props.ctaText,
            ctaAction: props.ctaAction,
        });
    }, [props]);

    const handleLegacyCheckoutErrors = useCallback(() => {
        if (
            props.statusCode &&
            props.statusCode in CART_STATUS_CODES &&
            !isEmpty(props.ctaText) &&
            !isEmpty(props.ctaAction)
        ) {
            return (
                <>
                    {props.children}
                    <ErrorWidgetContainer>
                        <ErrorWidget
                            statusCode={props.statusCode}
                            titleMessage={props.titleMessage}
                            statusMessage={props.statusMessage}
                            ctaText={props.ctaText}
                            ctaAction={props.ctaAction}
                            action={props.onErrorCtaAction}
                        />
                    </ErrorWidgetContainer>
                </>
            );
        }

        return renderGenericError();
    }, [
        props.children,
        props.ctaAction,
        props.ctaText,
        props.onErrorCtaAction,
        props.statusCode,
        props.statusMessage,
        props.titleMessage,
        renderGenericError,
    ]);

    if (!isEmpty(props.softError)) {
        return (
            <>
                {props.children}
                <ToastContainer>
                    <Toast
                        message={props.softError}
                        containerClass={"toast"}
                        show
                        autoDismiss
                        onDismiss={props.resetSoftError}
                    />
                </ToastContainer>
            </>
        );
    }

    if (!props.error) {
        return <>{props.children}</>;
    }

    switch (props.statusCode) {
        // If the error is due to the group cart no longer existing
        case GROUP_CART_STATUS_CODES.GROUP_CART_ORDER_PLACED:
            return (
                <>
                    {props.children}
                    <GroupCartBottomSheetInfo
                        title={ORDER_ON_THE_WAY_COMMS.TITLE}
                        subText={ORDER_ON_THE_WAY_COMMS.SUBTITLE}
                        onBackEvent={handleBack}
                    />
                </>
            );

        // If the error is due to the group cart no longer existing
        case GROUP_CART_STATUS_CODES.GROUP_CART_NO_LONGER_EXISTS:
            return (
                <>
                    {props.children}
                    <GroupCartBottomSheetInfo
                        title={GROUP_NO_LONGER_EXISTS_TEXT.titleMessage}
                        subText={GROUP_NO_LONGER_EXISTS_TEXT.statusMessage}
                        onBackEvent={handleBack}
                    />
                </>
            );

        // if the group cart is locked for updates,
        // no handling is required as such since toast is already shown to user as part of state update
        case GROUP_CART_STATUS_CODES.GROUP_CART_HAS_LOCKED:
            return <>{props.children}</>;

        case CART_STATUS_CODES.FAILURE:
        case CART_STATUS_CODES.SESSION_EXPIRED:
            return renderGenericError();

        default:
            return handleLegacyCheckoutErrors();
    }
};

export default React.memo(GroupCartErrorBoundary);
